import { useMemo, useState } from "react";
import {
  ChevronDown,
  Armchair,
  Bike,
  Package,
  Merge,
  Check,
  X,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { Order, OrderStatus } from "@/redux/services/order/order.type";
import { useMergeOrdersMutation } from "@/redux/services/order/order.api";
import { cn } from "@/lib/utils";
import { ORDERS_MANAGE_PERMISSIONS } from "@/lib/navigation";
import { usePermissions } from "@/hooks/usePermissions";
import OrderCard from "./OrderCard";

type GroupKind = "table" | "takeaway" | "delivery" | "other";

interface Props {
  label: string;
  kind: GroupKind;
  orders: Order[];
  searchOrderNumber: string;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

const icons: Record<GroupKind, React.ComponentType<{ className?: string }>> = {
  table: Armchair,
  takeaway: Package,
  delivery: Bike,
  other: Armchair,
};

// Active dine-in orders that can be folded together. Mirrors the API's
// MergeOrdersProcessor eligibility (SFRESTO-87).
const MERGEABLE_STATUSES: OrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
];

const OrderTableGroup: React.FC<Props> = ({
  label,
  kind,
  orders,
  searchOrderNumber,
  onModalOpen,
  onModalClose,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [mergeOrders, { isLoading: isMerging }] = useMergeOrdersMutation();
  const Icon = icons[kind];

  const mergeableIds = useMemo(
    () =>
      new Set(
        orders
          .filter((o) => MERGEABLE_STATUSES.includes(o.status))
          .map((o) => o.id),
      ),
    [orders],
  );

  // Merging only makes sense for two or more active orders sharing a table.
  // Merging rewrites orders: orders.manage, like the drawer's merge action
  // (SFRESTO-184).
  const { canAny } = usePermissions();
  const canMerge =
    kind === "table" &&
    mergeableIds.size >= 2 &&
    canAny(...ORDERS_MANAGE_PERMISSIONS);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleMerge = async () => {
    if (selectedIds.length < 2) return;
    try {
      await mergeOrders({ orderIds: selectedIds }).unwrap();
      toast.success("Commandes fusionnées");
      exitSelectMode();
    } catch {
      toast.error("Échec de la fusion des commandes");
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setIsCollapsed((v) => !v)}
          className="flex items-center gap-2 flex-1 min-w-0"
          aria-expanded={!isCollapsed}
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isCollapsed && "-rotate-90",
            )}
          />
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <h3 className="text-sm font-semibold text-card-foreground truncate">
            {label}
          </h3>
          <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {orders.length}
          </span>
          <span className="flex-1 border-b border-border ml-2" />
        </button>

        {canMerge && !selectMode && (
          <button
            type="button"
            onClick={() => setSelectMode(true)}
            className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors shrink-0"
          >
            <Merge className="h-3.5 w-3.5" />
            Fusionner
          </button>
        )}

        {selectMode && (
          <button
            type="button"
            onClick={exitSelectMode}
            className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <X className="h-3.5 w-3.5" />
            Annuler
          </button>
        )}
      </div>

      {selectMode && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <p className="text-sm text-card-foreground">
            Sélectionnez les commandes à fusionner
            {selectedIds.length > 0 && (
              <span className="text-muted-foreground">
                {" "}
                · {selectedIds.length} sélectionnée
                {selectedIds.length > 1 ? "s" : ""}
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={handleMerge}
            disabled={selectedIds.length < 2 || isMerging}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMerging ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Merge className="h-4 w-4" />
            )}
            Fusionner
          </button>
        </div>
      )}

      {!isCollapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {orders.map((order) => {
            const isMergeable = mergeableIds.has(order.id);
            const isSelected = selectedIds.includes(order.id);

            if (!selectMode) {
              return (
                <OrderCard
                  key={order.id}
                  order={order}
                  searchOrderNumber={searchOrderNumber}
                  onModalOpen={onModalOpen}
                  onModalClose={onModalClose}
                />
              );
            }

            return (
              <div
                key={order.id}
                className={cn(
                  "relative rounded-xl",
                  isSelected && "ring-2 ring-primary",
                  !isMergeable && "opacity-50",
                )}
              >
                {/* Overlay intercepts the card click while selecting so the
                    details drawer never opens; non-mergeable orders are inert. */}
                <button
                  type="button"
                  disabled={!isMergeable}
                  onClick={() => toggleSelect(order.id)}
                  aria-pressed={isSelected}
                  aria-label={
                    isMergeable
                      ? `Sélectionner la commande ${order.orderNumber}`
                      : `Commande ${order.orderNumber} non fusionnable`
                  }
                  className="absolute inset-0 z-10 rounded-xl disabled:cursor-not-allowed"
                >
                  {isMergeable && (
                    // Outside the card's corner: inside it, it covered the
                    // order total (SFRESTO-180).
                    <span
                      data-testid="merge-select-marker"
                      className={cn(
                        "absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white shadow-sm",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border",
                      )}
                    >
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                  )}
                </button>
                <OrderCard
                  order={order}
                  searchOrderNumber={searchOrderNumber}
                />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default OrderTableGroup;
